import React, { useState, useEffect, useRef, useContext } from 'react';
import { useTranslation } from "react-i18next";
import { useSelector } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import { Dropdown } from 'primereact/dropdown';
import { confirmDialog } from 'primereact/confirmdialog';
import CustomDropdown from '../../../custom/customDropdown';
import { AuthContext } from '../../../Header/auth - context';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const emptyForm = { id_vozilo: null, id_zaposleni: null, Kilometraza: null, pocetnaKM: null, zavrsnaKM: null, Napomena: '' };
const emptyBonus = { id_zaposleni: null, id_sifarnik: null, opis_bonus: '', iznos_eur: 0 };

const fmt = (val) => val == null ? '—' : Number(val).toLocaleString('sr-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const koefBg    = (k) => k >= 1.0 ? '#d4edda' : k >= 0.5 ? '#fff3cd' : '#f8f9fa';
const koefColor = (k) => k >= 1.0 ? '#155724' : k >= 0.5 ? '#856404' : '#6c757d';

const DodatniObracun = () => {
  const { t } = useTranslation();
    const toast = useRef(null);
    const auth = useContext(AuthContext);
    const company = useSelector(state => state.company);
    const trip = useSelector(state => state.trip);
    const prevTripRef = useRef(null);
    const autoImportAttemptedRef = useRef(false);

    const [activeTab, setActiveTab] = useState(1);
    const activeClass = 'activeButton';

    // Tab 0 — KILOMETRAZE
    const [kmDB, setKmDB] = useState([]);
    const [vozilaDB, setVozilaDB] = useState([]);
    const [zaposleniDB, setZaposleniDB] = useState([]);
    const [formData, setFormData] = useState(emptyForm);
    const [selectedId, setSelectedId] = useState(null);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [importing, setImporting] = useState(false);

    // Tab 1 — BONUSI
    const [turaBonusiDB, setTuraBonusiDB] = useState([]);
    const [bonusiSifarnikDB, setBonusiSifarnikDB] = useState([]);
    const [filteredBonusiSifarnik, setFilteredBonusiSifarnik] = useState([]);
    const [formBonus, setFormBonus] = useState(emptyBonus);
    const [selectedBonusId, setSelectedBonusId] = useState(null);
    const [selectedBonusVozac, setSelectedBonusVozac] = useState(null);

    // Tab 2 — DANI
    const [turaDaniDB, setTuraDaniDB] = useState([]);
    const [selectedDanVozac, setSelectedDanVozac] = useState(null);
    const [generatingDani, setGeneratingDani] = useState(false);
    const [generatingBonusi, setGeneratingBonusi] = useState(false);

    // ─── KILOMETRAZE helpers ───────────────────────────────────────

    const resetForm = () => {
        setFormData(emptyForm);
        setSelectedId(null);
        setFormSubmitted(false);
    };

    const fetchInit = (onSuccess) => {
        if (!trip) return;
        fetch(process.env.REACT_APP_BACKEND_URL + '/trip/GetDodatniObracunKm', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
            body: JSON.stringify({ id_putni_nalog: trip }),
        })
            .then(r => r.json())
            .then(data => {
                setKmDB(data.KmDB || []);
                setVozilaDB(data.VozilaDB || []);
                setZaposleniDB(data.ZaposleniDB || []);
                if (onSuccess) onSuccess(data);
            })
            .catch(err => {
                console.error('GetDodatniObracunKm error:', err);
                toast.current?.show({ severity: 'error', summary: 'Greška pri učitavanju!', life: 3000 });
            });
    };

    const doAutoImport = () => {
        setImporting(true);
        fetch(process.env.REACT_APP_BACKEND_URL + '/gps/ImportDrivesKm', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
            body: JSON.stringify({ id_putni_nalog: trip }),
        })
            .then(r => r.json())
            .then(data => {
                setImporting(false);

                /* Vožnja koju nijedno Kretanje ne pokriva se NE sme progutati u tišini:
                   kilometraža posle kraja Kretanja ostaje van obračuna, a operater to
                   vidi tek kad mu se ne poklopi odometar. Poruka stoji duže od ostalih
                   i ide zasebno po vozilu. */
                (data.upozorenja || []).forEach(poruka => toast.current?.show({
                    severity: 'warn', summary: t('ture.nepokrivenaVoznja'),
                    detail: poruka, life: 12000, sticky: false,
                }));

                /* `updated` postoji otkad je uvoz ponovljiv: ispravka postojećeg reda
                   ne povećava `inserted`, pa bi se bez ovoga tabela osvežila tek ručno
                   i operater bi gledao staru kilometražu uz poruku da je ispravljena. */
                if (data.inserted > 0 || data.updated > 0) {
                    toast.current?.show({ severity: 'success', summary: data.message || `Uvezeno ${data.inserted} zapisa!`, life: 4000 });
                    fetchInit();
                } else if (data.skipped > 0) {
                    toast.current?.show({ severity: 'warn', summary: data.message || 'Svi zapisi već postoje za ovu turu.', life: 5000 });
                } else {
                    toast.current?.show({ severity: 'info', summary: data.message || 'Nema vožnji za import.', life: 5000 });
                }
            })
            .catch(() => {
                setImporting(false);
                toast.current?.show({ severity: 'error', summary: 'Greška pri auto-importu kilometraže!', life: 3000 });
            });
    };

    // ─── BONUSI / DANI helpers ────────────────────────────────────

    const fetchSifarnikForVozac = (id_zaposleni) => {
        if (!id_zaposleni || !trip) { setFilteredBonusiSifarnik([]); return; }
        apiPost({ step: 'GET_SIFARNIK_FOR_VOZAC', id_putni_nalog: trip, id_zaposleni })
            .then(data => setFilteredBonusiSifarnik(data.BonusiSifarnikDB || []))
            .catch(() => setFilteredBonusiSifarnik([]));
    };

    const fetchBonusiDani = () => {
        if (!trip) return;
        fetch(process.env.REACT_APP_BACKEND_URL + '/trip/ManageTuraBonusiDani', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
            body: JSON.stringify({ step: 'GET_DATA', id_putni_nalog: trip }),
        })
            .then(r => r.json())
            .then(data => {
                setBonusiSifarnikDB(data.BonusiSifarnikDB || []);
                setTuraBonusiDB(data.TuraBonusiDB || []);
                setTuraDaniDB(data.TuraDaniDB || []);
            })
            .catch(err => console.error('ManageTuraBonusiDani GET_DATA error:', err));
    };

    const apiPost = (body) =>
        fetch(process.env.REACT_APP_BACKEND_URL + '/trip/ManageTuraBonusiDani', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
            body: JSON.stringify(body),
        }).then(r => r.json());

    // ─── useEffect ────────────────────────────────────────────────

    useEffect(() => {
        if (!trip) {
            toast.current?.show({ severity: 'warn', summary: 'Upozorenje!', detail: 'Niste izabrali putni nalog!', life: 3000 });
            return;
        }
        if (trip !== prevTripRef.current) {
            prevTripRef.current = trip;
            autoImportAttemptedRef.current = false;
            resetForm();
            setFormBonus(emptyBonus);
            setSelectedBonusId(null);
            setSelectedBonusVozac(null);
            setSelectedDanVozac(null);
            setTuraBonusiDB([]);
            setTuraDaniDB([]);
            setBonusiSifarnikDB([]);
            setFilteredBonusiSifarnik([]);
        }
        fetchInit(data => {
            if (!data.KmDB || data.KmDB.length === 0) {
                if (!data.VozilaDB || data.VozilaDB.length === 0) {
                    toast.current?.show({
                        severity: 'warn',
                        summary: 'Nema kretanja za ovu turu. Unesite kretanje pre importa kilometraže.',
                        life: 5000,
                    });
                    return;
                }
                if (!autoImportAttemptedRef.current) {
                    autoImportAttemptedRef.current = true;
                    doAutoImport();
                }
            }
        });
        fetchBonusiDani();
    }, [trip]); // eslint-disable-line

    // ─── KILOMETRAZE handlers ─────────────────────────────────────

    const handleRowSelect = (row) => {
        const id = row?.id;
        setSelectedId(prev => {
            const next = String(prev) === String(id) ? null : id;
            if (next !== null) {
                setFormData({
                    id_vozilo:    row.id_vozilo    != null ? Number(row.id_vozilo)    : null,
                    id_zaposleni: row.id_zaposleni != null ? Number(row.id_zaposleni) : null,
                    Kilometraza:  row.Kilometraza  != null ? Number(row.Kilometraza)  : null,
                    pocetnaKM:    row.pocetnaKM    != null ? Number(row.pocetnaKM)    : null,
                    zavrsnaKM:    row.zavrsnaKM    != null ? Number(row.zavrsnaKM)    : null,
                    Napomena:     row.Napomena || '',
                });
            } else {
                setFormData(emptyForm);
                setFormSubmitted(false);
            }
            return next;
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const next = { ...prev, [name]: value };
            const poc = name === 'pocetnaKM' ? value : next.pocetnaKM;
            const zav = name === 'zavrsnaKM' ? value : next.zavrsnaKM;
            if (poc != null && zav != null) {
                const diff = Number(zav) - Number(poc);
                next.Kilometraza = diff >= 0 ? diff : next.Kilometraza;
            }
            return next;
        });
    };

    const handleSave = (e) => {
        e.preventDefault();
        setFormSubmitted(true);
        const { id_vozilo, id_zaposleni, Kilometraza } = formData;
        if (!id_vozilo || !id_zaposleni || Kilometraza == null) {
            toast.current?.show({ severity: 'warn', summary: 'Popunite obavezna polja!', life: 3000 });
            return;
        }
        confirmDialog({
            message: selectedId ? 'Sačuvati izmene?' : 'Dodati novi zapis?',
            acceptLabel: 'Da', rejectLabel: 'Ne',
            icon: 'pi pi-info-circle', acceptClassName: 'p-button-danger',
            accept: () => doSave(),
        });
    };

    const doSave = () => {
        fetch(process.env.REACT_APP_BACKEND_URL + '/trip/ManageDodatniObracunKm', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
            body: JSON.stringify({ step: 'SAVE', id: selectedId || 0, id_putni_nalog: trip, formData }),
        })
            .then(r => r.json())
            .then(data => {
                const msg = data[0]?.message || '';
                if (msg.includes('Nema izmena')) {
                    toast.current?.show({ severity: 'warn', summary: msg, life: 3000 });
                    return;
                }
                toast.current?.show({ severity: 'success', summary: msg || 'Uspešno!', life: 3000 });
                resetForm();
                fetchInit();
            })
            .catch(() => toast.current?.show({ severity: 'error', summary: 'Greška pri čuvanju!', life: 3000 }));
    };

    const handleDelete = () => {
        if (!selectedId) return;
        confirmDialog({
            message: 'Obrisati izabrani zapis?',
            acceptLabel: 'Da', rejectLabel: 'Ne',
            icon: 'pi pi-info-circle', acceptClassName: 'p-button-danger',
            accept: () => {
                fetch(process.env.REACT_APP_BACKEND_URL + '/trip/ManageDodatniObracunKm', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
                    body: JSON.stringify({ step: 'DELETE', id: selectedId, id_putni_nalog: trip }),
                })
                    .then(r => r.json())
                    .then(() => {
                        toast.current?.show({ severity: 'success', summary: 'Zapis obrisan!', life: 3000 });
                        resetForm();
                        fetchInit();
                    })
                    .catch(() => toast.current?.show({ severity: 'error', summary: 'Greška pri brisanju!', life: 3000 }));
            },
        });
    };

    const handleManualImport = () => {
        if (!trip) return;
        autoImportAttemptedRef.current = false;
        doAutoImport();
    };

    const exportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Dodatni obracun km');
        const headers = [t('nova.vozilo'), t('ture.vozac'), t('ture.drzava'), t('ture.kilometraza'), t('ture.pocetnaKM'), t('ture.zavrsnaKM'), t('nova.napomena')];
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true };
        headerRow.eachCell(cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } };
            cell.border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
        });
        (kmDB || []).forEach(r => {
            const row = worksheet.addRow([
                r.VoziloNaziv  || '',
                r.ZaposleniNaziv || '',
                r.DrzavaOznaka || '',
                r.Kilometraza != null ? Number(r.Kilometraza) : '',
                r.pocetnaKM != null ? Number(r.pocetnaKM) : '',
                r.zavrsnaKM != null ? Number(r.zavrsnaKM) : '',
                r.Napomena || '',
            ]);
            row.eachCell(cell => {
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        });
        worksheet.columns.forEach(col => {
            let max = 10;
            col.eachCell(cell => { if (cell.value) max = Math.max(max, String(cell.value).length); });
            col.width = max + 2;
        });
        const dt = new Date().toLocaleString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Belgrade' }).replace(/,/g, '');
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `Dodatni obracun km - ${company?.companyName || ''} - ${dt}.xlsx`);
    };

    const invalid = (val) => formSubmitted && (val === null || val === '' || val === undefined);

    const tableHeader = (
        <div className="export-buttons fakture-stavke-header">
            <Tooltip target=".export-buttons>button" position="bottom" />
            <div className="fakture-stavke-radnje">
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded onClick={exportExcel} data-pr-tooltip="XLS" />
                {selectedId && <Button type="button" icon="pi pi-trash" severity="danger" rounded onClick={handleDelete} data-pr-tooltip="Obriši" />}
                <Button type="button" icon="pi pi-download" severity="info" rounded
                    onClick={handleManualImport} loading={importing}
                    data-pr-tooltip="Import km iz Mobilisisa" />
            </div>
            <input type="search" placeholder={t('common.search')} className="p-inputtext p-component"
                onInput={e => setGlobalFilter(e.target.value)} />
        </div>
    );

    // ─── BONUSI handlers ──────────────────────────────────────────

    const handleAddBonus = () => {
        if (!formBonus.id_zaposleni || !formBonus.opis_bonus?.trim()) {
            toast.current?.show({ severity: 'warn', summary: 'Vozač i opis su obavezni!', life: 3000 });
            return;
        }
        apiPost({ step: 'SAVE_BONUS', id: 0, id_putni_nalog: trip, ...formBonus })
            .then(result => {
                const row = Array.isArray(result) ? result[0] : result;
                if (row?.message?.includes('Nema izmena')) {
                    toast.current?.show({ severity: 'warn', summary: row.message, life: 3000 });
                    return;
                }
                toast.current?.show({ severity: 'success', summary: row?.message || 'Bonus dodat!', life: 3000 });
                setFormBonus({ id_zaposleni: formBonus.id_zaposleni, opis_bonus: '', iznos_eur: 0 });
                fetchBonusiDani();
            })
            .catch(() => toast.current?.show({ severity: 'error', summary: 'Greška pri dodavanju!', life: 3000 }));
    };

    const handleUpdateBonus = () => {
        if (!formBonus.opis_bonus?.trim()) {
            toast.current?.show({ severity: 'warn', summary: 'Opis je obavezan!', life: 3000 });
            return;
        }
        apiPost({ step: 'SAVE_BONUS', id: selectedBonusId, id_putni_nalog: trip, ...formBonus })
            .then(result => {
                const row = Array.isArray(result) ? result[0] : result;
                if (row?.message?.includes('Nema izmena')) {
                    toast.current?.show({ severity: 'warn', summary: row.message, life: 3000 });
                    return;
                }
                toast.current?.show({ severity: 'success', summary: row?.message || 'Bonus ažuriran!', life: 3000 });
                setSelectedBonusId(null);
                setFormBonus(emptyBonus);
                fetchBonusiDani();
            })
            .catch(() => toast.current?.show({ severity: 'error', summary: 'Greška pri ažuriranju!', life: 3000 }));
    };

    const handleDeleteBonus = (id) => {
        confirmDialog({
            message: 'Obrisati ovaj bonus?',
            acceptLabel: 'Da', rejectLabel: 'Ne',
            icon: 'pi pi-info-circle', acceptClassName: 'p-button-danger',
            accept: () =>
                apiPost({ step: 'DELETE_BONUS', id, id_putni_nalog: trip })
                    .then(() => {
                        toast.current?.show({ severity: 'success', summary: 'Bonus obrisan!', life: 3000 });
                        setSelectedBonusId(null);
                        setFormBonus(emptyBonus);
                        setTuraBonusiDB(prev => prev.filter(b => b.id !== id));
                    })
                    .catch(() => toast.current?.show({ severity: 'error', summary: 'Greška pri brisanju!', life: 3000 })),
        });
    };

    const handleBonusSelect = (b) => {
        setSelectedBonusId(b.id);
        setFormBonus({ id_zaposleni: b.id_zaposleni, id_sifarnik: b.id_sifarnik ?? null, opis_bonus: b.opis_bonus, iznos_eur: b.iznos_eur });
        fetchSifarnikForVozac(b.id_zaposleni);
    };

    const imaAutoBonuseZaVozaca = (id_zaposleni) =>
        (turaBonusiDB || []).some(b => Number(b.id_zaposleni) === Number(id_zaposleni) && b.id_sifarnik != null);

    const handleGenerateBonusi = (id_zaposleni) => {
        const imaGenerisaneDane = (turaDaniDB || []).some(d => Number(d.id_zaposleni) === Number(id_zaposleni));
        if (!imaGenerisaneDane) {
            toast.current?.show({ severity: 'warn', summary: 'Najpre generišite dane za ovog vozača!', life: 4000 });
            return;
        }
        if (imaAutoBonuseZaVozaca(id_zaposleni)) {
            toast.current?.show({ severity: 'warn', summary: 'Vozač već ima generisane bonuse. Obrišite ih ručno pre ponovnog generisanja.', life: 4000 });
            return;
        }
        setGeneratingBonusi(id_zaposleni);
        apiPost({ step: 'GENERATE_BONUSI', id_putni_nalog: trip, id_zaposleni })
            .then(result => {
                setGeneratingBonusi(false);
                const rows = Array.isArray(result) ? result : [];
                if (rows[0]?.message) {
                    toast.current?.show({ severity: 'warn', summary: rows[0].message, life: 4000 });
                    return;
                }
                fetchBonusiDani();
                toast.current?.show({ severity: 'success', summary: `Generisano ${rows.length} bonusa!`, life: 3000 });
            })
            .catch(() => {
                setGeneratingBonusi(false);
                toast.current?.show({ severity: 'error', summary: 'Greška pri generisanju bonusa!', life: 3000 });
            });
    };

    // ─── DANI handlers ────────────────────────────────────────────

    const doGenerateDani = () => {
        setGeneratingDani(true);
        apiPost({ step: 'GENERATE_DANI', id_putni_nalog: trip, id_zaposleni: selectedDanVozac })
            .then(result => {
                setGeneratingDani(false);
                const warning = !Array.isArray(result) && result?.warning ? result.warning : null;
                const rows = Array.isArray(result) ? result : (Array.isArray(result?.rows) ? result.rows : []);
                if (warning) {
                    toast.current?.show({ severity: 'warn', summary: warning, life: 5000 });
                }
                setTuraDaniDB(prev => [
                    ...prev.filter(d => Number(d.id_zaposleni) !== Number(selectedDanVozac)),
                    ...rows,
                ]);
                toast.current?.show({ severity: 'success', summary: `Generisano ${rows.length} dana!`, life: 3000 });
            })
            .catch(() => {
                setGeneratingDani(false);
                toast.current?.show({ severity: 'error', summary: 'Greška pri generisanju!', life: 3000 });
            });
    };

    const handleGenerateDani = () => {
        if (!selectedDanVozac) {
            toast.current?.show({ severity: 'warn', summary: 'Izaberite vozača!', life: 3000 });
            return;
        }
        if (daniForVozac.length > 0) {
            confirmDialog({
                message: 'Vozač već ima generisane dane. Brisanjem se poništavaju sve ručne izmene koeficijenata. Nastaviti?',
                header: 'Re-generisanje dana',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'Da, re-generisi',
                rejectLabel: 'Odustani',
                accept: () => {
                    setGeneratingDani(true);
                    apiPost({ step: 'DELETE_ALL_DANI', id_putni_nalog: trip, id_zaposleni: selectedDanVozac })
                        .then(() => {
                            setTuraDaniDB(prev => prev.filter(d => Number(d.id_zaposleni) !== Number(selectedDanVozac)));
                            doGenerateDani();
                        })
                        .catch(() => {
                            setGeneratingDani(false);
                            toast.current?.show({ severity: 'error', summary: 'Greška pri brisanju dana!', life: 3000 });
                        });
                },
            });
            return;
        }
        doGenerateDani();
    };

    const handleToggleDan = (danRow) => {
        const nextKoef = danRow.koeficijent >= 1.0 ? 0.0 : danRow.koeficijent >= 0.5 ? 1.0 : 0.5;
        apiPost({ step: 'SAVE_DAN', id: danRow.id, koeficijent: nextKoef })
            .then(() => {
                setTuraDaniDB(prev => prev.map(d => d.id === danRow.id ? { ...d, koeficijent: nextKoef } : d));
            })
            .catch(() => toast.current?.show({ severity: 'error', summary: 'Greška pri ažuriranju dana!', life: 3000 }));
    };

    // ─── DANI calendar ────────────────────────────────────────────

    const daniForVozac = (turaDaniDB || []).filter(d => Number(d.id_zaposleni) === Number(selectedDanVozac));

    const daniWeeks = (() => {
        if (!daniForVozac.length) return [];
        const isoDow = dStr => { const d = new Date(dStr).getDay(); return d === 0 ? 7 : d; };
        const weeks = [];
        let week = new Array(isoDow(daniForVozac[0].datum) - 1).fill(null);
        daniForVozac.forEach(d => {
            week.push(d);
            if (week.length === 7) { weeks.push(week); week = []; }
        });
        if (week.length) { while (week.length < 7) week.push(null); weeks.push(week); }
        return weeks;
    })();

    const totalDaniKoef = daniForVozac.reduce((sum, d) => sum + Number(d.koeficijent || 0), 0);

    // filtriranje bonusa po izabranom vozacu
    const bonusiPrikaz = selectedBonusVozac
        ? turaBonusiDB.filter(b => Number(b.id_zaposleni) === Number(selectedBonusVozac))
        : turaBonusiDB;

    const totalBonusa = bonusiPrikaz.reduce((sum, b) => sum + Number(b.iznos_eur || 0), 0);

    // ─── RENDER ───────────────────────────────────────────────────

    return (
        <div>
            <Toast ref={toast} />
            <Button id={activeTab === 1 && activeClass} label={t('ture.kilometraze')} onClick={() => setActiveTab(1)} />
            <Button id={activeTab === 3 && activeClass} label={t('ture.dani')} className="mt-3 zaposleniShift" onClick={() => setActiveTab(3)} />
            <Button id={activeTab === 2 && activeClass} label={t('ture.bonusi')} className="mt-3 zaposleniShift" onClick={() => setActiveTab(2)} />

            {/* ═══════════════ TAB 1: KILOMETRAŽE ═══════════════ */}
            {activeTab === 1 && <div>
                    {importing && (
                        <div className="obracun-info">
                            Uvoz kilometraže iz Mobilisisa u toku...
                        </div>
                    )}
                    <div className="blagajnaDashboard dnevniceDashboard">
                        <Card>
                            <form autoComplete="off">
                                <div className="gridLicni">
                                    <div className="col-12 md:col-4" style={{ display: 'flex', flexDirection: 'column' }}>
                                        <label>{t('nova.vozilo')}:</label>
                                        <CustomDropdown name="id_vozilo" value={formData.id_vozilo}
                                            options={vozilaDB} optionLabel="label" placeholder={t('nova.phVozilo')}
                                            filter showClear
                                            invalid={invalid(formData.id_vozilo)}
                                            onChange={handleChange} />
                                    </div>
                                    <div className="col-12 md:col-4" style={{ display: 'flex', flexDirection: 'column' }}>
                                        <label>{t('ture.vozac')}:</label>
                                        <CustomDropdown name="id_zaposleni" value={formData.id_zaposleni}
                                            options={zaposleniDB} optionLabel="label" placeholder={t('ture.phVozaca')}
                                            filter showClear
                                            invalid={invalid(formData.id_zaposleni)}
                                            onChange={handleChange} />
                                    </div>
                                    <div className="col-12 md:col-4" style={{ display: 'flex', flexDirection: 'column' }}>
                                        <label>{t('ture.kilometraza')}:</label>
                                        <InputNumber name="Kilometraza" value={formData.Kilometraza}
                                            onValueChange={handleChange} minFractionDigits={2} maxFractionDigits={2}
                                            className={invalid(formData.Kilometraza) ? 'p-invalid' : ''} />
                                    </div>
                                    <div className="col-12 md:col-4" style={{ display: 'flex', flexDirection: 'column' }}>
                                        <label>{t('ture.pocetnaKM')}:</label>
                                        <InputNumber name="pocetnaKM" value={formData.pocetnaKM}
                                            onValueChange={handleChange} minFractionDigits={2} maxFractionDigits={2} />
                                    </div>
                                    <div className="col-12 md:col-4" style={{ display: 'flex', flexDirection: 'column' }}>
                                        <label>{t('ture.zavrsnaKM')}:</label>
                                        <InputNumber name="zavrsnaKM" value={formData.zavrsnaKM}
                                            onValueChange={handleChange} minFractionDigits={2} maxFractionDigits={2} />
                                    </div>
                                    <div className="col-12" style={{ display: 'flex', flexDirection: 'column' }}>
                                        <label>{t('nova.napomena')}:</label>
                                        <InputText name="Napomena" value={formData.Napomena} onChange={handleChange} />
                                    </div>
                                </div>
                                <div className="formUnosZap buttonUnosZap">
                                    <button type="submit" onClick={handleSave} style={{ minWidth: 'fit-content', marginTop: '2%' }} className="btn btn-success">
                                        {selectedId ? 'SAČUVAJ' : 'UNOS'}
                                    </button>
                                </div>
                                {/* Boje idu kroz klase (`--ui-*` tokeni), ne kroz inline `style`:
                                    inline boja ne zna za temu, pa je u tamnoj ovo zaglavlje
                                    ostajalo svetlo sivo sa svetlim tekstom — necitljivo. */}
                                {zaposleniDB.length > 0 && (
                                    <table className="km-zbir">
                                        <thead>
                                            <tr>
                                                <th>{t('ture.vozac')}</th>
                                                <th className="desno">{t('ture.ukupnoKm')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {zaposleniDB.map(z => {
                                                const totalKm = (kmDB || [])
                                                    .filter(k => Number(k.id_zaposleni) === Number(z.value))
                                                    .reduce((sum, k) => sum + (Number(k.Kilometraza) || 0), 0);
                                                return (
                                                    <tr key={z.value}>
                                                        <td>{z.label}</td>
                                                        <td className="desno zbir">{totalKm.toFixed(2)} km</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </form>
                        </Card>
                        <Card>
                            <DataTable className="stavke-tabela" value={kmDB || []} paginator rows={10} scrollable scrollHeight="45vh"
                                header={tableHeader} emptyMessage="Nema zapisa" resizableColumns
                                globalFilter={globalFilter}
                                rowClassName={row => String(row.id) === String(selectedId) ? 'selected-row' : ''}>
                                <Column header={t('nova.izaberi')} style={{ minWidth: '90px' }}
                                    body={rowData => (
                                        <Button label={t('nova.izaberi')} icon="pi pi-eye" type="button"
                                            className="p-button-text p-button-sm" onClick={() => handleRowSelect(rowData)} />
                                    )} />
                                <Column field="VoziloNaziv" header={t('nova.vozilo')} sortable style={{ minWidth: '140px' }} />
                                <Column field="ZaposleniNaziv" header={t('ture.vozac')} sortable style={{ minWidth: '160px' }} />
                                <Column field="DrzavaOznaka" header={t('ture.drzava')} sortable style={{ minWidth: '90px' }}
                                    body={r => r.DrzavaOznaka || '-'} />
                                <Column field="Kilometraza" header={t('ture.kilometraza')} sortable style={{ minWidth: '120px' }}
                                    body={r => r.Kilometraza != null ? `${Number(r.Kilometraza).toFixed(2)} km` : '-'} />
                                <Column field="pocetnaKM" header={t('ture.pocetnaKM')} sortable style={{ minWidth: '120px' }}
                                    body={r => r.pocetnaKM != null ? Number(r.pocetnaKM).toFixed(2) : '-'} />
                                <Column field="zavrsnaKM" header={t('ture.zavrsnaKM')} sortable style={{ minWidth: '120px' }}
                                    body={r => r.zavrsnaKM != null ? Number(r.zavrsnaKM).toFixed(2) : '-'} />
                                <Column field="Napomena" header={t('nova.napomena')} style={{ minWidth: '200px' }}
                                    body={r => r.Napomena || '-'} />
                            </DataTable>
                        </Card>
                    </div>
            </div>}

            {/* ═══════════════ TAB 2: BONUSI ═══════════════ */}
            {activeTab === 2 && <div style={{ marginTop: 8 }}>
                        {/* Forma za unos */}
                        <Card style={{ marginBottom: 12 }}>
                            <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 14 }}>
                                {selectedBonusId ? 'Izmena bonusa' : 'Novi bonus'}
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
                                <div style={{ minWidth: 220 }}>
                                    <label style={{ display: 'block', marginBottom: 3 }}>{t('ture.vozacReq')}</label>
                                    <Dropdown
                                        value={formBonus.id_zaposleni}
                                        options={zaposleniDB}
                                        onChange={e => {
                                            setFormBonus(p => ({ ...p, id_zaposleni: e.value, id_sifarnik: null, opis_bonus: '', iznos_eur: 0 }));
                                            fetchSifarnikForVozac(e.value);
                                        }}
                                        placeholder={t('ture.phVozaca')}
                                        showClear filter
                                        style={{ width: '100%' }} />
                                </div>
                                <div style={{ minWidth: 230 }}>
                                    <label style={{ display: 'block', marginBottom: 3 }}>{t('ture.vrstaBonusa')}</label>
                                    <Dropdown
                                        value={formBonus.id_sifarnik}
                                        options={filteredBonusiSifarnik}
                                        disabled={!formBonus.id_zaposleni}
                                        onChange={e => {
                                            const izabran = filteredBonusiSifarnik.find(b => b.value === e.value);
                                            setFormBonus(p => ({
                                                ...p,
                                                id_sifarnik: e.value,
                                                opis_bonus: izabran ? izabran.label : p.opis_bonus,
                                                iznos_eur: izabran ? Number(izabran.Iznos || 0) : p.iznos_eur,
                                            }));
                                        }}
                                        placeholder={formBonus.id_zaposleni ? 'Izaberi vrstu (opciono)' : 'Najpre izaberite vozača'}
                                        showClear filter
                                        style={{ width: '100%' }} />
                                </div>
                                <div style={{ minWidth: 240 }}>
                                    <label style={{ display: 'block', marginBottom: 3 }}>{t('ture.opisReq')}</label>
                                    <InputText
                                        value={formBonus.opis_bonus}
                                        onChange={e => setFormBonus(p => ({ ...p, opis_bonus: e.target.value }))}
                                        placeholder={t('ture.opisBonusa')}
                                        style={{ width: '100%' }} />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: 3 }}>{t('ture.iznosEur')}</label>
                                    <InputNumber
                                        value={formBonus.iznos_eur}
                                        onValueChange={e => setFormBonus(p => ({ ...p, iznos_eur: e.value }))}
                                        mode="decimal" minFractionDigits={2} maxFractionDigits={2}
                                        inputStyle={{ width: 110, textAlign: 'right' }} />
                                </div>
                                <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
                                    {selectedBonusId ? (
                                        <>
                                            <Button label={t('ture.azuriraj')} icon="pi pi-check"
                                                severity="warning" onClick={handleUpdateBonus} />
                                            <Button label={t('nova.odustani')} icon="pi pi-times"
                                                severity="secondary"
                                                onClick={() => { setSelectedBonusId(null); setFormBonus(emptyBonus); setFilteredBonusiSifarnik([]); }} />
                                        </>
                                    ) : (
                                        <Button label={t('common.add')} icon="pi pi-plus"
                                            severity="success" onClick={handleAddBonus} />
                                    )}
                                </div>
                            </div>
                        </Card>

                        {/* Filter po vozacu + Generiši bonuse */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                            <span className="bonusi-prazno">Filter / vozač:</span>
                            <Dropdown
                                value={selectedBonusVozac}
                                options={zaposleniDB}
                                onChange={e => setSelectedBonusVozac(e.value)}
                                placeholder={t('ture.sviVozaci')}
                                showClear
                                style={{ width: 220 }} />
                            <Button
                                label={t('ture.generisiBonuse')}
                                icon="pi pi-bolt"
                                size="small"
                                severity="info"
                                loading={!!generatingBonusi}
                                disabled={!!generatingBonusi || !selectedBonusVozac || imaAutoBonuseZaVozaca(selectedBonusVozac)}
                                title={selectedBonusVozac && imaAutoBonuseZaVozaca(selectedBonusVozac) ? 'Vozač već ima generisane bonuse' : undefined}
                                onClick={() => handleGenerateBonusi(selectedBonusVozac)} />
                        </div>

                        {/* Tabela bonusa */}
                        {bonusiPrikaz.length > 0 ? (
                            <table className="bonusi-tabela">
                                <thead>
                                    <tr>
                                        {['Vozač', 'Opis', 'Iznos €', ' '].map((h, i) => (
                                            <th key={i} className={i >= 2 ? 'desno' : undefined}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {bonusiPrikaz.map(b => (
                                        <tr key={b.id} className={b.id === selectedBonusId ? 'izabran' : undefined}>
                                            <td>{b.VozacNaziv}</td>
                                            <td>{b.opis_bonus}</td>
                                            <td className="desno iznos">{fmt(b.iznos_eur)}</td>
                                            <td className="sredina">
                                                <Button icon="pi pi-pencil" size="small" severity="warning"
                                                    className="p-button-text" onClick={() => handleBonusSelect(b)} />
                                                <Button icon="pi pi-times" size="small" severity="danger"
                                                    className="p-button-text" onClick={() => handleDeleteBonus(b.id)} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="ukupno">
                                        <td colSpan={2}>UKUPNO</td>
                                        <td className="desno">{fmt(totalBonusa)}</td>
                                        <td />
                                    </tr>
                                </tfoot>
                            </table>
                        ) : (
                            <p className="bonusi-prazno">Nema bonusa za ovu turu.</p>
                        )}
            </div>}

            {/* ═══════════════ TAB 3: DANI ═══════════════ */}
            {activeTab === 3 && <div style={{ marginTop: 8 }}>
                        {/* Odabir vozača i GENERISI */}
                        <Card style={{ marginBottom: 12 }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 12 }}>
                                <div style={{ minWidth: 240 }}>
                                    <label style={{ display: 'block', marginBottom: 3 }}>{t('ture.vozac')}</label>
                                    <Dropdown
                                        value={selectedDanVozac}
                                        options={zaposleniDB}
                                        onChange={e => setSelectedDanVozac(e.value)}
                                        placeholder={t('ture.phVozaca')}
                                        showClear filter
                                        style={{ width: '100%' }} />
                                </div>
                                <Button
                                    label={daniForVozac.length > 0 ? 'RE-GENERISI' : 'GENERISI'}
                                    icon="pi pi-cog"
                                    severity={daniForVozac.length > 0 ? 'warning' : 'primary'}
                                    loading={generatingDani}
                                    disabled={!selectedDanVozac}
                                    onClick={handleGenerateDani} />
                            </div>
                        </Card>

                        {/* Kalendar */}
                        {selectedDanVozac && daniForVozac.length > 0 ? (
                            <div>
                                <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>
                                    Dani rada — ukupno: <strong>{Number(totalDaniKoef).toFixed(1)}</strong> dana
                                    <span className="bonusi-prazno" style={{ marginLeft: 8 }}>
                                        | Klik = promeni (0 → 0.5 → 1.0 → 0)
                                    </span>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 5, maxWidth: 560 }}>
                                    {['Pon', 'Uto', 'Sre', 'Čet', 'Pet', 'Sub', 'Ned'].map((lbl, i) => (
                                        <div key={lbl} style={{
                                            fontSize: 10, fontWeight: 700, textAlign: 'center',
                                            color: i >= 5 ? '#dc3545' : '#6c757d',
                                        }}>{lbl}</div>
                                    ))}
                                    {daniWeeks.map((week, wi) =>
                                        week.map((d, ci) => {
                                            if (!d) return <div key={`${wi}-${ci}`} />;
                                            const isWknd = ci >= 5;
                                            return (
                                                <button key={d.id} type="button"
                                                    onClick={() => handleToggleDan(d)}
                                                    style={{
                                                        border: `1px solid ${isWknd ? '#adb5bd' : '#ced4da'}`,
                                                        borderRadius: 4, padding: '3px 6px',
                                                        cursor: 'pointer',
                                                        background: isWknd && d.koeficijent === 0 ? '#e9ecef' : koefBg(d.koeficijent),
                                                        color: koefColor(d.koeficijent),
                                                        fontWeight: 600, fontSize: 11, textAlign: 'center',
                                                    }}>
                                                    <div style={{ fontSize: 10, color: isWknd ? '#868e96' : 'inherit' }}>
                                                        {d.datumPrikaz?.slice(0, 5)}
                                                    </div>
                                                    <div>{d.koeficijent === 0 ? '—' : Number(d.koeficijent).toFixed(1)}</div>
                                                </button>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        ) : selectedDanVozac ? (
                            <p className="bonusi-prazno">
                                Nema generisanih dana za ovog vozača. Kliknite GENERISI.
                            </p>
                        ) : (
                            <p className="bonusi-prazno">Izaberite vozača.</p>
                        )}
            </div>}

        </div>
    );
};

export default DodatniObracun;
